<template>
  <MainLayout>
    <div>
      <v-container class="mt-3" style="max-width: 90%">
        <v-data-table
          v-model:search="search"
          class="elevation-4"
          :headers="headers"
          :items="projects"
          items-per-page="5"
          :hide-default-footer="projects.length <= 5"
          :sort-by="[{ key: 'id', order: 'desc' }]"
        >
        <template v-slot:top>
            <v-toolbar
              rounded
              style="background-color: #0d47a1; color: white"
            >
              <!-- TOOLBAR TITLE WITH ADD PROJECT BUTTON -->
              <v-toolbar-title class="font-weight-bold">
                My Projects

                <AddNewProject :rules="projectNamePresent" descriptive-name="Project" :items="projects" />

              </v-toolbar-title>

              <v-text-field
                  class="mx-4"
                  :rounded="true"
                  max-width="37%"
                  density="compact"
                  v-model="search"
                  bg-color="#d8d8f4"
                  variant="solo-filled"
                  label="Search"
                  hide-details
                  prepend-inner-icon="fas fa-search"
                  single-line
              />
            </v-toolbar>

          </template>
          <template v-slot:item.actions="{ item }">
            <div>

              <!-- DETAILS PROJECT BUTTON -->
              <DetailsActionButton
                  :route="$routes.project(item.id)"
                  :item="item" descriptive-word="Project" />

              <!-- MODAL FOR UPLOADING KPIs/PSIs -->
              <LoadListDialog :project="item" :available-files="{ kpi: availableKpis, psi: availablePsis }" />

              <!-- CLONE MODAL -->
              <CloneActionButton
                  :rules="projectNamePresent"
                  :success-fallback="$routes.projects()"
                  :route="$routes.project_clone"
                  descriptive-name="project" :items="projects" :item="item" />

              <DeleteActionButton
                :item="item"
                item-type="Project"
                :route="$routes.project(item.id)"
                :success-fallback="$routes.projects()"
              />
            </div>
          </template>

          <template v-slot:item.compare="{ item }">
            <v-checkbox
              class="d-flex justify-center"
              @change="addToCompare(item.id, item.name)"
            >
            </v-checkbox>
          </template>
        </v-data-table>

        <v-divider class="my-4 border-opacity-75"></v-divider>

        <v-col cols="12" v-if="projectsCompareList.length > 0">
          <ComparableProjectsList :projects-compare-list="projectsCompareList">
          </ComparableProjectsList>
        </v-col>

        <v-row v-if="projects.length < 1">
          <v-col>
            <p style="color: #949ca3">
              There are no projects in this Account. Click the <i class="fa fa-plus" /> <b>ADD PROJECT</b> button to
              begin.
            </p>
          </v-col>
        </v-row>

        <v-alert
            v-if="alertMessage.length > 0"
            @click:close="onAlertClose"
            v-model="alertState"
            closable
            variant="tonal"
            color="red-darken-3"
            icon="fa fa-warning"
            class="mb-10"
        >
          <div v-html="alertMessage" />
        </v-alert>
      </v-container>
    </div>
  </MainLayout>
</template>

<script setup>
import MainLayout from "../../Layouts/Main.vue";
import ComparableProjectsList from "./subcomponents/ComparableProjectsList.vue";
import LoadListDialog from "~/Pages/Projects/subcomponents/LoadListDialog.vue";
import AddNewProject from "./subcomponents/AddNewProject.vue";
import {ref} from "vue";
import { router } from "@inertiajs/vue3";
import * as $routes from "@/packs/routes";
import DeleteActionButton from "@/Shared/DeleteActionButton.vue";
import DetailsActionButton from "@/Shared/DetailsActionButton.vue";
import CloneActionButton from "@/Shared/CloneActionButton.vue";

const search = ref("");

const props = defineProps({
  projects: {
    type: Array,
    required: true,
  },
  alert: {
    type: Array,
    required: false,
    default: []
  },
  project: {
    type: Object,
    required: true,
  },
  availableKpis: {
    type: Array,
    required: true,
  },
  availablePsis: {
    type: Array,
    required: false,
  },
  notice: {
    type: String,
    required: false
  }
});



let compareProjects = ref(false);
let projectsCompareList = ref([]);
let addProjectDialog = ref(false);
let newProjectName = ref('');

const projects = ref(props.projects.map((project) => ({
  ...project,
  updated_at: formatDate(project.updated_at),
})));

const projectNamePresent = [
  (value) => {
    if (value) return true;
    return "Project name cannot be blank.";
  },

  (value) => {
    if (props.projects.some((project) => project.name === value)) return "This project name already exists.";
    return true;
  }
];

const addToCompare = (id, name) => {
  const newProjectIndex = projectsCompareList.value.findIndex(
    (p) => p.id === id
  );
  if (newProjectIndex < 0 && projectsCompareList.value.length < 5) {
    projectsCompareList.value.push({ id, name });
  } else {
    projectsCompareList.value.splice(newProjectIndex, 1);
  }
};

const headers = [
  {
    title: "ID",
    value: "id",
    align: "start",
    sortable: true,
  },
  { title: "Name", value: "name", sortable: true },
  { title: "Last Updated At", value: "updated_at", sortable: true },
  { title: "Actions", value: "actions", align: "center" },
  { title: "Compare", value: "compare", align: "center" },
];

/**
 * Handles the view/hide state of the alert box.
 * Required because if the user manually closes the alert
 * and then a new error occurs, the alert will not be
 * rendered anew.
 */
const alertState = ref(true)
const alertMessage = ref('')

function formatDate(dateString) {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

/**
 * Check `alertState` doc for details.
 */
function onAlertClose() {
  alertState.value = true
  alertMessage.value = ''
}

router.on('success', (event) => {
  if(props.alert && props.alert.length > 0) {
    alertMessage.value = props.alert;
  }
})

</script>

<style lang="css" scoped>
.comparison-label {
  font-size: 15px;
  padding: 8px 10px 5px 10px;
  background: #2b28d2;
  color: white;
  border-radius: 5px 5px 0 0;
  cursor: pointer;
}

.comparison-container {
  padding: 10px 55px 10px 15px;
  border-radius: 20px;
  background: #2926d6;
  color: white;
  margin-top: 15px;
  margin-bottom: 15px;
}

.comparison-button {
  color: black;
  background: #cfd7ff;
  padding: 5px;
  border-radius: 5px;
  text-decoration: none;
  margin-left: 10px;
}
::v-deep tbody tr:nth-child(even) {
  background-color: #eeeeee7f; /* Style for odd rows */
}

::v-deep tbody td {
  font-size: medium;
}
</style>
